import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import { queryClient } from './lib/queryClient'
import { scrubBreadcrumb, scrubSentryEvent } from './lib/sentryScrub'

// Import the generated route tree
import { routeTree } from './routeTree.gen'

// Sentry 초기화 — 가장 이른 시점에 실행
Sentry.init({
  dsn: 'https://d8fd76a751a384beedeea0a8c2dcd2dc@o4508871682097152.ingest.us.sentry.io/4511160509988864',
  environment: import.meta.env.MODE, // 'development' | 'production'
  release: import.meta.env.VITE_APP_VERSION || 'local',
  sendDefaultPii: true,
  // 개발 + 프로덕션 모두 에러 전송
  enabled: true,
  // 인앱 브라우저(인스타그램·카카오 등)가 페이지에 주입한 네이티브 브릿지
  // 스크립트가 내는 에러. 우리 소스에는 없는 함수인데(sendDataToNative /
  // sendPageHideMessage) window.onerror 로 잡혀 우리 프로젝트에 쌓인다.
  // 체험단 공개 링크가 인스타그램에서 열리는 경우가 많아 7일 149건까지 나왔다.
  // URL 로 받는 토큰(?token= 세션 JWT, ?sso_token=, #access_token=)이 에러 이벤트·breadcrumb 으로
  // 나가지 않게 전송 직전에 가린다 — 백엔드 sentry_scrub.py 와 같은 역할 (src/lib/sentryScrub.ts)
  beforeSend: (event) => scrubSentryEvent(event),
  beforeSendTransaction: (event) => scrubSentryEvent(event),
  beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
  ignoreErrors: [
    /window\.webkit\.messageHandlers/,
    /sendDataToNative/,
    /sendPageHideMessage/,
    // Android WebView 브릿지가 파괴된 뒤 postMessage 를 호출할 때
    'Java object is gone',
  ],
})

// Create a new router instance
const router = createRouter({ routeTree })

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('root')
if (rootElement) {
  const root = createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        {/* 개발 환경에서만 React Query 디버그 패널 표시 */}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </StrictMode>,
  )
} else {
  console.error('Root element not found!')
}
